import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { boardKind, latestSnapshot } from "../../../lib/api/fieldApi";
import { isStageMachine } from "../../stage";
import { useFieldApi } from "../../../hooks/useFieldApi";
import { useMyNickname } from "../../../hooks/useMyNickname";
import { ButtonLink } from "../../../components/ui/Button";
import { PicoBubble } from "../../../components/PicoBubble";
import { ScreenRoot } from "../../../components/Shell";
import { useTopBar } from "../../../components/TopBar";
import { WorkCard } from "../../../components/WorkCard";
import { useDensity } from "../../../store/density";
import { useI18n } from "../../../i18n";
import { avatarEmoji, useMyAvatar } from "../../pico";
import { glbUrlFor } from "../../../lib/preview";
import { useRoomApi } from "../../room";
import { lessonChip, workTitle } from "../../upload";
import type { MineLoad } from "../types";
import { useMissionApi } from "../../../lib/api/missionApi";

/**
 * FS-01-02 놀이터 입구(학생 홈) — SCR v0.6 FS-01 와이어.
 *  - 상단 바: '🌿 놀이터 입구 · SE 모드 · 5차시 · 오르막 자동차'(차시 칩은 현재 작품의 차시가 있을 때만).
 *  - 피코 인사 '오늘은 뭐 하고 놀까?' · '내 기계' 카드(현재 스냅샷 썸네일·작품명·원리 태그·올려짐 ✓) · '⬆ 새로 올리기'.
 *  - 활동 타일: SE 는 아이콘 우선 큰 타일 2×2(전시관 · 코스 · 우리 반 룸(PH2 흐림) · 나의 발자취) + 아바타 버튼,
 *    SU 는 '타일 5개 한 줄 · 텍스트 라벨'(… + 내 아바타).
 *  - 작품 없음: 타일 흐림 + 피코 '먼저 기계를 올려 보자'. 점수·순위·자유 입력 없음(DR-01/02).
 *  - 4.6 §4.3 Campus 미션: 묶인 미션에 확인되지 않은 목표가 있으면 '🎯 미션 이어 하기'(→ /course · FS-04-01 카드).
 *  - P3-S4 교류 룸(SCR v0.7 검토 §4 FS-01-02 변형): '🏟 옆 반과 함께 놀아요' 배너 · 룸 타일 입장 → /room(반 비교 없음).
 */
export function HomeScreen() {
  const { t, locale } = useI18n();
  const { density } = useDensity();
  const api = useFieldApi();
  // 작품명의 별명은 서버 별명 — 세션 displayName(LTI 실명일 수 있음)은 쓰지 않는다
  const name = useMyNickname() ?? "";
  const se = density === "se";
  const [mine, setMine] = useState<MineLoad>({ status: "loading" });
  // 마이크로비트 놀이터(PICNIC-MB-FIELD-001 1단계) — 내 Bench 작품에 micro:bit 이 있을 때만 타일을 보인다(SPIKE 반에는 안 보임)
  const [hasMicrobit, setHasMicrobit] = useState(false);
  useEffect(() => {
    if (!api) return;
    let alive = true;
    api.sources().then(
      (s) => alive && setHasMicrobit(s.bench.some((b) => boardKind(b.board) === "microbit")),
      () => {},
    );
    return () => {
      alive = false;
    };
  }, [api]);
  const { avatar } = useMyAvatar();
  // P2-S1: 선생님이 룸을 열었으면 타일이 켜지고 피코가 알린다(작품이 없어도 구경은 된다)
  const roomApi = useRoomApi();
  const [roomOpen, setRoomOpen] = useState(false);
  const [exchange, setExchange] = useState(false);
  // 4.6 — 확인되지 않은 미션 목표가 있는가(묶음이 없거나 못 받으면 false)
  const missionApi = useMissionApi();
  const [missionLeft, setMissionLeft] = useState(false);
  useEffect(() => {
    if (!missionApi) return;
    let cancelled = false;
    void missionApi.mission().then((m) => {
      if (!cancelled) setMissionLeft(!!m && m.goals.some((g) => g.state !== "verified"));
    });
    return () => {
      cancelled = true;
    };
  }, [missionApi]);
  useEffect(() => {
    if (!roomApi) return;
    let cancelled = false;
    roomApi
      .room()
      .then((r) => {
        if (cancelled) return;
        const on = !!r && r.state !== "closed";
        setRoomOpen(on);
        setExchange(on && r!.kind === "exchange");
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [roomApi]);

  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    // 팔레트는 작품 제 appearance.palette 이름으로(2026-09-30 팔레트 버전 전환 — 옛 brick-v1 작품은 brick-v1 색)
    void (async () => {
      const snapshot = latestSnapshot(await api.mySnapshots().catch(() => []));
      const palette = await api.palette(snapshot?.appearance?.palette ?? null);
      if (!cancelled) setMine({ status: "ready", snapshot, palette });
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  const snapshot = mine.status === "ready" ? mine.snapshot : null;
  const empty = mine.status === "ready" && !snapshot;
  const chip = lessonChip(t, locale, snapshot?.lesson);
  useTopBar({
    icon: "🌿",
    title: t("student.home.title"),
    chips: chip ? [{ label: chip, testId: "lesson-chip" }] : [],
  });

  const uploadLink = (
    <ButtonLink to="/upload" variant="accent" icon="⬆" className="pf-home__upload">
      {t("student.home.upload")}
    </ButtonLink>
  );

  /** 활동 타일 — 작품이 없으면 전시관·코스는 흐린 안내 타일(누를 수 없음) */
  const tile = (
    to: string,
    icon: string,
    label: string,
    variant: "green" | "ghost",
    dim = false,
  ) =>
    dim ? (
      <span className="pf-btn pf-btn--dim pf-tile" aria-disabled="true" data-tile={to}>
        {se && (
          <span className="pf-btn__icon" aria-hidden="true">
            {icon}
          </span>
        )}
        <span className="pf-btn__label">{label}</span>
      </span>
    ) : (
      <ButtonLink
        to={to}
        variant={variant}
        icon={se ? icon : undefined}
        className="pf-tile"
        data-tile={to}
      >
        {label}
      </ButtonLink>
    );

  const avatarLink = (extra?: string): ReactNode => (
    <ButtonLink
      to="/avatar"
      variant="ghost"
      icon={se ? avatarEmoji(avatar) : undefined}
      className={extra}
      data-tile="/avatar"
    >
      {t("student.home.avatar")}
    </ButtonLink>
  );

  return (
    <ScreenRoot id="FS-01-02" title={t("student.home.title")} hideTitle className="pf-home-screen">
      <div className="pf-pane pf-pane--hero pf-home">
        <div className="pf-home__greet">
          <PicoBubble
            text={
              roomOpen
                ? t(exchange ? "student.home.exchangePico" : "room.openBanner")
                : empty
                  ? t("student.home.noMachine")
                  : t("student.home.pico")
            }
            live={roomOpen}
          />
          {se && avatarLink("pf-home__avatar")}
        </div>
        {missionLeft && (
          <div
            className="pf-banner pf-banner--info pf-home__mission"
            role="status"
            data-testid="home-mission"
          >
            <span className="pf-banner__icon" aria-hidden="true">
              🎯
            </span>
            <div className="pf-banner__text">{t("student.home.mission")}</div>
            <ButtonLink
              to={isStageMachine(snapshot) ? "/stage" : "/course"}
              variant="accent"
              data-testid="home-mission-continue"
            >
              {t("student.home.missionContinue")}
            </ButtonLink>
          </div>
        )}
        {exchange && (
          <div
            className="pf-banner pf-banner--ok pf-home__exchange"
            role="status"
            data-testid="home-exchange"
          >
            <span className="pf-banner__icon" aria-hidden="true">
              🏟
            </span>
            <div className="pf-banner__text">{t("student.home.exchange")}</div>
            <ButtonLink to="/room" variant="accent" data-testid="home-exchange-enter">
              {t("student.home.exchangeEnter")}
            </ButtonLink>
          </div>
        )}
        {snapshot && mine.status === "ready" ? (
          <WorkCard
            work={{
              ...snapshot,
              title: workTitle(t, locale, snapshot, name),
              glbUrl: glbUrlFor(snapshot),
            }}
            palette={mine.palette}
            heading={t("student.home.myMachine")}
          >
            {uploadLink}
          </WorkCard>
        ) : (
          <div className="pf-machine" aria-busy={mine.status === "loading"}>
            <div className="pf-machine__thumb" aria-hidden="true" />
            <div className="pf-machine__body">
              <h2 className="pf-work__heading">{t("student.home.myMachine")}</h2>
              {empty && <p className="pf-work__subtitle">{t("student.home.noMachineShort")}</p>}
              {uploadLink}
            </div>
          </div>
        )}
        <nav
          className={`pf-tiles pf-tiles--${density}`}
          aria-label={t("student.home.activities")}
          data-testid="home-tiles"
        >
          {tile("/gallery", "🏛", t("student.home.gallery"), "green", empty)}
          {tile("/course", "⛰", t("student.home.course"), "green", empty)}
          {isStageMachine(snapshot) && tile("/stage", "🎡", t("student.home.stage"), "green")}
          {hasMicrobit && tile("/microbit", "🟥", t("student.microbit.tile"), "green")}
          {roomOpen ? (
            <ButtonLink
              to="/room"
              variant="accent"
              icon={se ? "🏟" : undefined}
              className="pf-tile"
              data-tile="room"
              data-open="true"
            >
              {t(exchange ? "student.home.exchangeTile" : "room.enter")}
            </ButtonLink>
          ) : (
            <span className="pf-btn pf-btn--dim pf-tile" aria-disabled="true" data-tile="room">
              {se && (
                <span className="pf-btn__icon" aria-hidden="true">
                  🏟
                </span>
              )}
              <span className="pf-btn__label">{t("student.home.room")}</span>
            </span>
          )}
          {tile("/me", "👣", t("student.home.footprints"), "ghost")}
          {!se && avatarLink("pf-tile")}
        </nav>
      </div>
    </ScreenRoot>
  );
}
